import {
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Link,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Input,
  Text as ChakraText,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiDeleteBinFill } from 'react-icons/ri'
import { NavLink } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { PermissionsEnum } from '../../../utils'
import useToast from '../../hooks/useToast'
import type { MultiFactorAuthentication } from '../../interfaces/MultiFactorAuthentication'
import { MainContentWrapper } from '../../layouts/Base/MainContent'
import { UserContext } from '../../providers'
import { InfoPopover } from '../ExternalStorage/InfoPopover'
import PermissionGuard from '../PermissionGuard'
import ChangePasswordModal from './ChangePasswordModal'
import DeleteAccountModal from './DeleteAccountModal'
import { SessionTable } from './SessionTable'

const UserDetails = (): React.ReactElement => {
  const {
    user,
    refetch: refetchUser,
    loading: loadingUser,
  } = useContext(UserContext)
  const { t } = useTranslation()
  const { request: updateRequest } = useFetch({ cachePolicy: CachePolicies.NO_CACHE, manual: true } as any)
  const [isEditingUserName, setIsEditingUserName] = useState(false)
  const [newUserName, setNewUserName] = useState(user?.userName || '')
  const [updatingUserName, setUpdatingUserName] = useState(false)
  
  const handleUserNameSave = async () => {
    if (user?.id) {
      const trimmedUserName = newUserName.trim();
      if (!trimmedUserName) return;
      setUpdatingUserName(true);
      try {
        await updateRequest.post(`/api/user/${user.id}/update-username`, {
          userName: trimmedUserName,
        })
        if (updateRequest.error) {
          toast({
            description: t('failed_to_update_username', 'Failed to update name'),
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
          return
        }
        await refetchUser()
        toast({
          description: t('username_updated', 'Name updated successfully'),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        setIsEditingUserName(false)
      } finally {
        setUpdatingUserName(false)
      }
    }
  }
  const [multiFA, setMultiFA] = useState<boolean>(
    user?.multiFactorAuth ?? false,
  )
  useEffect(() => {
    setMultiFA(user?.multiFactorAuth ?? false)
  }, [user?.multiFactorAuth])
  const toast = useToast()
  const {
    isOpen: isOpenChangePassword,
    onOpen: onOpenChangePassword,
    onClose: onCloseChangePassword,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteAccount,
    onOpen: onOpenDeleteAccount,
    onClose: onCloseDeleteAccount,
  } = useDisclosure()

  const { request, loading: loadingMFAUpdate } =
    useFetch<MultiFactorAuthentication>({
      cachePolicy: CachePolicies.NO_CACHE,
    })
  const onChange = async (): Promise<void> => {
    if (user?.id) {
      await request.post(`/api/user/${user.id}/multi-factor-auth`, {
        multiFactorAuth: !multiFA,
      })
      if (request.error) {
        toast({
          description: t('something_went_wrong', 'Something went wrong!'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }
      await refetchUser()

      toast({
        description: t(
          'successfully_updated_multifactor_authentication',
          'Successfully updated multi-factor authentication',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }

  return (
    <React.Fragment>
      <MainContentWrapper>
        <HStack spacing="2em" justify="flex-start" align="flex-start">
          <Avatar size="xl" name={user?.userName} />
          <Stack spacing={5} flex="1">
            {isEditingUserName ? (
              <form onSubmit={(e) => { e.preventDefault(); handleUserNameSave(); }}>
                <Stack spacing={1}>
                  <HStack spacing={2}>
                    <Input
                      value={newUserName}
                      onChange={(e) => setNewUserName(e.target.value)}
                      size="sm"
                      autoFocus
                      isInvalid={newUserName.trim() === ''}
                    />
                    <Button type="submit" size="sm" isLoading={updatingUserName} isDisabled={newUserName.trim() === '' || newUserName.trim() === user?.userName.trim()}>
                      {t('save', 'Save')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setNewUserName(user?.userName || ''); setIsEditingUserName(false); }}>
                      {t('cancel', 'Cancel')}
                    </Button>
                  </HStack>
                  {newUserName.trim() === '' && (
                    <ChakraText color="red.500" fontSize="sm">
                      Name cannot be empty
                    </ChakraText>
                  )}
                </Stack>
              </form>
            ) : (
              <HStack spacing={2}>
                <Heading
                  as="h5"
                  size="md"
                  fontWeight="600"
                  color={colors.appDarkIndigo}
                >
                  {user?.userName}
                </Heading>
                <Button size="sm" variant={"outline"} onClick={() => setIsEditingUserName(true)}>
                  {t('edit', 'Edit')}
                </Button>
              </HStack>
            )}
            <Divider />
            <SimpleGrid columns={4} spacing={5}>
              <UserDetaillsInfoItem
                name={t('user_role', 'Role')}
                value={user?.roleDescription}
              />
              <UserDetaillsInfoItem
                name={t('processing_region', 'Processing Region')}
                value={user?.region}
              />
            </SimpleGrid>
            <SimpleGrid columns={4} spacing={5}>
              <UserDetaillsInfoItem
                name={t('member_since', 'Member Since')}
                value={moment(user?.createdAt).format('DD MMMM YYYY')}
              />
              <UserDetaillsInfoItem
                name={t('imagekit_id', 'ImageKit ID')}
                value={user?.imagekitId}
              />
            </SimpleGrid>
            <Divider />
            <Grid
              templateColumns="repeat(4, minmax(0px, 1fr))"
              columnGap={5}
              rowGap={5}
            >
              <GridItem>
                <ChakraText flex="1" fontWeight="bold">
                  {t('email', 'Email')}
                </ChakraText>
              </GridItem>
              <GridItem>
                <ChakraText flex="4" fontWeight={600} color={colors.appSlate}>
                  {user?.userEmail}
                </ChakraText>
              </GridItem>
              <GridItem>
                <Flex justifyContent="left">
                  <ChakraText fontWeight="bold">
                    {t(
                      'multifactor_authentication',
                      'Multi-factor authentication',
                    )}
                  </ChakraText>
                  <InfoPopover
                    info="user_details_multifactor_authentication_desc"
                    hyperlink="Learn More."
                    link="https://docs.imagekit.io/features/single-sign-on"
                  />
                </Flex>
              </GridItem>
              <GridItem>
                <Tooltip
                  hasArrow
                  placement="bottom"
                  label={
                    user?.globalMultiFactorAuth
                      ? 'Multi-factor authentication is forced enabled by account admin.'
                      : null
                  }
                >
                  <PermissionGuard
                    permission={PermissionsEnum.SELF_USER_MANAGE}
                  >
                    {(hasPermission: boolean) => (
                      <Flex alignItems="baseline">
                        <Switch
                          isDisabled={
                            !hasPermission ||
                            loadingUser ||
                            loadingMFAUpdate ||
                            user?.globalMultiFactorAuth
                          }
                          isChecked={multiFA}
                          onChange={onChange}
                        />
                        {loadingUser || loadingMFAUpdate ? (
                          <Spinner boxSize="4" ml="2" />
                        ) : null}
                      </Flex>
                    )}
                  </PermissionGuard>
                </Tooltip>
              </GridItem>
              <GridItem>
                <ChakraText fontWeight="bold" flex="1">
                  {t('password', 'Password')}
                </ChakraText>
              </GridItem>
              <GridItem>
                {user?.isPasswordSetByUser && (
                  <ChakraText fontWeight={600} flex="4" color={colors.appSlate}>
                    ********
                  </ChakraText>
                )}
                <Link onClick={onOpenChangePassword}>
                  {user?.isPasswordSetByUser
                    ? t('change_password', 'Change Password')
                    : t('set_password', 'Set Password')}
                </Link>
              </GridItem>
              <GridItem>
                <PermissionGuard permission={PermissionsEnum.SELF_TEAM_MANAGE}>
                  {(hasPermission: boolean) =>
                    hasPermission ? (
                      <Link
                        as={NavLink}
                        lineHeight="base"
                        to="/settings/multi-factor-auth"
                        _focus={{ textDecoration: 'none', outline: 'none' }}
                      >
                        {t('enable_global_MFA', 'Enable global MFA')}
                      </Link>
                    ) : (
                      <></>
                    )
                  }
                </PermissionGuard>
              </GridItem>
            </Grid>
            <PermissionGuard permission={PermissionsEnum.SELF_USER_VIEW}>
              {(hasPermission: boolean) =>
                hasPermission ? <SessionTable /> : <></>
              }
            </PermissionGuard>
            <PermissionGuard permission={PermissionsEnum.SELF_CLIENT_MANAGE}>
              {(hasPermission: boolean) =>
                hasPermission ? (
                  <Box>
                    <Button
                      mt="2em"
                      leftIcon={<RiDeleteBinFill />}
                      variant="deleteCTA"
                      onClick={onOpenDeleteAccount}
                    >
                      {t('delete_account', 'Delete Account')}
                    </Button>
                  </Box>
                ) : (
                  <></>
                )
              }
            </PermissionGuard>
          </Stack>
        </HStack>
      </MainContentWrapper>
      <ChangePasswordModal
        isOpen={isOpenChangePassword}
        onClose={onCloseChangePassword}
      />
      <DeleteAccountModal
        isOpen={isOpenDeleteAccount}
        onClose={onCloseDeleteAccount}
        user={user}
      />
    </React.Fragment>
  )
}

export default UserDetails

export const UserDetaillsInfoItem = ({
  name,
  value,
}: {
  name: string
  value: string | undefined
}): React.ReactElement => (
  <>
    <ChakraText fontWeight="bold">{name}</ChakraText>
    <ChakraText fontWeight={600} color={colors.appSlate}>
      {value}
    </ChakraText>
  </>
)
